import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SkeletonText,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { ISubscription, noop, routes } from '@blockpulse3/data/shared';
import { useGetSubscriptionQuery } from '@blockpulse3/graphql/hooks';
import { formatDate, formatNationality } from '@blockpulse3/helpers';
import {
  ErrorQueryCard,
  ResponsiveModal,
  ResponsiveModalFooter,
  ResponsiveModalProps,
} from '@blockpulse3/ui/commons';

type Props = {
  /* ** Callback on Cancel button ** */
  onCancel?: () => void;
  /* ** Callback on Modify button ** */
  onModify?: () => void;
  /* ** Participation infos ** */
  subscriptionId: ISubscription['id'];
} & Omit<ResponsiveModalProps, 'children'>;

export function UserVerificationModal({
  subscriptionId,
  onCancel = noop,
  onModify = noop,
  ...props
}: Props): JSX.Element {
  /* ** Modal ** */
  const i18nInvitationsVerifyUserModal = useTranslations('Invitations.VerificationModal');
  const i18nInvitationsVerifyUserCard = useTranslations('Invitations.VerificationModal.UserCard');

  const navigate = useNavigate();

  const { data, loading, error } = useGetSubscriptionQuery({
    variables: { subscriptionId: subscriptionId || '' },
    skip: !subscriptionId,
  });

  const { subscription } = data || {};
  const { id = '' } = subscription || {};

  const handleVerificationValidate = (): void => {
    navigate(routes.me.subscription.fmt(id));
  };

  return (
    <ResponsiveModal {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{i18nInvitationsVerifyUserModal('VerifyInformations')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody as={Stack} spacing="4">
          <Stack fontSize="sm" spacing="0">
            <Text fontWeight="500">{i18nInvitationsVerifyUserModal('ModalDescription-1')}</Text>
            <Text fontWeight="500">
              {i18nInvitationsVerifyUserModal.rich('ModalDescription-2', {
                important: (chunks) => (
                  <Text as="span" fontWeight="700" textDecoration="underline">
                    {chunks}
                  </Text>
                ),
              })}
            </Text>
          </Stack>
          {!error ? (
            <Card bg="gray.50" boxShadow="none">
              <CardHeader>
                <Text color="gray.700" fontWeight="600">
                  {i18nInvitationsVerifyUserCard('ModalCardTitle')}
                </Text>
              </CardHeader>
              <CardBody as={Stack} spacing="4">
                <Divider />
                <Stack fontSize="sm" spacing="0">
                  <Text fontWeight="500">{i18nInvitationsVerifyUserCard('PhysicalPerson')}</Text>
                  <SkeletonText isLoaded={!loading} noOfLines={1} skeletonHeight="4">
                    <Text fontWeight="600">{subscription?.identity?.individualIdentity?.name}</Text>
                  </SkeletonText>
                </Stack>
                <Divider />
                <Stack fontSize="sm" spacing="0">
                  <Text fontWeight="500">{i18nInvitationsVerifyUserCard('BirthDate')}</Text>
                  <SkeletonText isLoaded={!loading} noOfLines={1} skeletonHeight="4">
                    <Text fontWeight="600">
                      {formatDate(
                        subscription?.identity?.individualIdentity?.birthdate,
                        'D MMMM YYYY',
                      )}
                    </Text>
                  </SkeletonText>
                </Stack>
                <Divider />
                <Stack fontSize="sm" spacing="0">
                  <Text fontWeight="500">{i18nInvitationsVerifyUserCard('BirthPlace')}</Text>
                  <SkeletonText isLoaded={!loading} noOfLines={1} skeletonHeight="4">
                    <Text fontWeight="600">
                      {subscription?.identity?.individualIdentity?.birthplace}
                    </Text>
                  </SkeletonText>
                </Stack>
                <Divider />
                <Stack fontSize="sm" spacing="0">
                  <Text fontWeight="500">{i18nInvitationsVerifyUserCard('Address')}</Text>
                  <SkeletonText isLoaded={!loading} noOfLines={1} skeletonHeight="4">
                    <Text fontWeight="600">
                      {subscription?.identity?.individualIdentity?.address?.formatted}
                    </Text>
                  </SkeletonText>
                </Stack>
                <Divider />
                <Stack fontSize="sm" spacing="0">
                  <Text fontWeight="500">{i18nInvitationsVerifyUserCard('Nationality')}</Text>
                  <SkeletonText isLoaded={!loading} noOfLines={1} skeletonHeight="4">
                    <Text fontWeight="600">
                      {formatNationality(
                        subscription?.identity?.individualIdentity?.nationality || '',
                      )}
                    </Text>
                  </SkeletonText>
                </Stack>
              </CardBody>
            </Card>
          ) : (
            <ErrorQueryCard h="360px" mt="4" />
          )}
        </ModalBody>
        <Divider />
        <ResponsiveModalFooter>
          <Button variant="secondary" onClick={onCancel}>
            {i18nInvitationsVerifyUserModal('Cancel')}
          </Button>
          <Button variant="secondary" onClick={onModify}>
            {i18nInvitationsVerifyUserModal('Modify')}
          </Button>
          <Button isDisabled={!!error || loading} onClick={handleVerificationValidate}>
            {i18nInvitationsVerifyUserModal('GoNext')}
          </Button>
        </ResponsiveModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
}

export type UserVerificationModalProps = Props;
